import React from 'react';
import { Alert, Button, Collapse, Space, Typography } from 'antd';
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';

import { GapList } from './DataCoverage';
import ProposalCard from './ProposalCard';
import { dataBadgeLabel, isBlocker, plural } from './format';
import { Message, ProposalCard as ProposalCardData, Step } from './types';

export type CardActions = {
  onOpen: (versionId: number) => void;
  onSave: (card: ProposalCardData) => void;
  onReject: (card: ProposalCardData) => void;
  onAsk: (card: ProposalCardData) => void;
};

/**
 * The tools the agent called, whatever they are: each shows its own name and
 * summary, so a tool this screen has never heard of still reads.
 */
export const StepList: React.FC<{ steps: Step[] }> = ({ steps }) => (
  <ol className="blueprint-builder__steps">
    {steps.map((step, index) => (
      <li key={`${step.tool}-${index}`}>
        {step.ok ? (
          <CheckCircleFilled
            className="blueprint-builder__step-ok"
            aria-label="ok"
          />
        ) : (
          <ExclamationCircleFilled
            className="blueprint-builder__step-warn"
            aria-label="did not succeed"
          />
        )}
        <Typography.Text code>{step.tool || 'step'}</Typography.Text>{' '}
        <span>{step.summary}</span>
      </li>
    ))}
  </ol>
);

const time = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

type Props = {
  message: Message;
  proposals: ProposalCardData[];
  /** Sends a quick reply as an ordinary user message. */
  onReply?: (text: string) => void;
  /** While a turn runs nothing more can be sent. */
  replyDisabled?: boolean;
} & CardActions;

/**
 * One chat message. Text is plain text with its line breaks kept; an
 * assistant message adds the data gaps of the proposals it shows (blockers
 * first), its tool steps (collapsed), a card for each proposal it references
 * and, last, any clarifying question with its quick replies. A quick reply
 * sends its message like the composer does; typing an answer works as well.
 * An `error` message is an error.
 */
const ChatMessage: React.FC<Props> = ({
  message,
  proposals,
  onReply,
  replyDisabled = false,
  ...actions
}) => {
  if (message.role === 'error') {
    return (
      <div className="blueprint-builder__message blueprint-builder__message--error">
        <Alert
          type="error"
          showIcon
          title="The builder could not finish this turn"
          description={
            <span className="blueprint-builder__text">{message.text}</span>
          }
        />
      </div>
    );
  }
  const user = message.role === 'user';
  const refs = message.proposal_refs || [];
  const steps = message.steps || [];
  const gaps = message.gaps || [];
  const blockers = gaps.filter(isBlocker).length;
  const question = message.question?.text ? message.question : null;
  const options = (question?.options || []).filter(
    option => option && (option.label || option.message),
  );
  return (
    <div
      className={`blueprint-builder__message blueprint-builder__message--${message.role}`}
      data-testid={`message-${message.id}`}
    >
      <div className="blueprint-builder__speaker">
        <Typography.Text strong>{user ? 'You' : 'Builder'}</Typography.Text>{' '}
        <Typography.Text type="secondary">
          {time(message.created_at)}
        </Typography.Text>
      </div>
      {message.text && (
        <div className="blueprint-builder__text">{message.text}</div>
      )}
      {gaps.length > 0 && (
        <div
          className="blueprint-builder__message-gaps"
          data-testid={`message-gaps-${message.id}`}
        >
          <Typography.Text
            type="secondary"
            className="blueprint-builder__label"
          >
            {dataBadgeLabel(
              blockers ? 'blocked' : 'partial',
              gaps.length,
              blockers,
            )}
          </Typography.Text>
          <GapList gaps={gaps} compact />
        </div>
      )}
      {!user && steps.length > 0 && (
        <Collapse
          ghost
          size="small"
          className="blueprint-builder__how"
          items={[
            {
              key: 'steps',
              label: `How I got here (${plural(steps.length, 'step')})`,
              children: <StepList steps={steps} />,
            },
          ]}
        />
      )}
      {refs.length > 0 && (
        <div className="blueprint-builder__cards">
          {refs.map(ref => {
            const card = proposals.find(item => item.id === ref.proposal_id);
            return card ? (
              <ProposalCard
                key={`${ref.proposal_id}-${ref.version_id}`}
                card={card}
                versionId={ref.version_id}
                {...actions}
              />
            ) : (
              <Typography.Text
                key={`${ref.proposal_id}-${ref.version_id}`}
                type="secondary"
              >
                Proposal #{ref.proposal_id} is not in this chat any more.
              </Typography.Text>
            );
          })}
        </div>
      )}
      {question && (
        <div
          className="blueprint-builder__question"
          data-testid={`question-${message.id}`}
        >
          <Typography.Text strong className="blueprint-builder__text">
            {question.text}
          </Typography.Text>
          {options.length > 0 && (
            <Space
              size={[6, 6]}
              wrap
              className="blueprint-builder__quick-replies"
              role="group"
              aria-label="Quick replies"
            >
              {options.map((option, index) => (
                <Button
                  key={`${option.label}-${index}`}
                  size="small"
                  disabled={replyDisabled || !onReply || !option.message}
                  onClick={() => onReply?.(option.message)}
                >
                  {option.label || option.message}
                </Button>
              ))}
            </Space>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
